import { LoadingIndicator } from '../LoadingIndicator';
import { ChevronIcon, Icon } from '@vrplatform/icons';
import type { ThemeColor } from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';
import {
  Center,
  Group,
  Text,
  Tooltip,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import type { JSX } from 'react';
import { Badge } from '../Badge';
import { Select, type SelectInputProps, type SelectProps } from '../select';
import type { SelectItem } from '../select/_types';

type InputSelectProps<TSelectValue extends string> =
  SelectProps<TSelectValue> & {
    customMultiPill?: (item: SelectItem<TSelectValue>) => JSX.Element;
    customSingleText?: (o: {
      item: SelectItem<TSelectValue> | null;
    }) => JSX.Element;
  };

export const InputSelect = <TSelectValue extends string = string>(
  props: InputSelectProps<TSelectValue>
) => {
  const { customMultiPill, customSingleText, ...selectProps } = props;

  return (
    <Select {...selectProps}>
      {(target) => (
        <InputSelectTarget
          {...target}
          customMultiPill={customMultiPill}
          customSingleText={customSingleText}
        />
      )}
    </Select>
  );
};

export const InputSelectTarget = <TSelectValue extends string = string>({
  setValue,
  disabled,
  loadingMutation,
  loadingQuery,
  opened,
  placeholder,
  withClearButton,
  withBorder = true,
  type,
  value,
  width,
  customMultiPill: CustomMultiPill,
  customSingleText: CustomSingleText,
  placeholderColor,
  error,
  backgroundColor,
  lineClamp,
  color: c,
  handlers: _,
  ...props
}: SelectInputProps<TSelectValue> & {
  customMultiPill?: (item: SelectItem<TSelectValue>) => JSX.Element;
  customSingleText?: (o: {
    item: SelectItem<TSelectValue> | null;
  }) => JSX.Element;
}) => {
  const loading = loadingMutation || loadingQuery;

  const { colors } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  const color: ThemeColor = c || 'gray';
  const clearButtonLabel =
    placeholder === 'Select posting date' || placeholder?.startsWith('Default:')
      ? 'Clear posting date'
      : 'Clear selection';

  const target = (
    // biome-ignore lint/correctness/useUniqueElementIds: want to have static id
    <Group
      wrap="nowrap"
      justify="space-between"
      id="input-target"
      {...props}
      sx={(theme) => {
        const isDarkTheme = colorScheme === 'dark';

        const borderColor = error ? 'red' : theme.primaryColor;

        return {
          width: width || undefined,
          border: '1px solid',
          borderColor: opened
            ? theme.colors[borderColor][6]
            : withBorder
              ? error
                ? theme.colors.red[6]
                : theme.colors[color][isDarkTheme ? 8 : 4]
              : 'transparent',
          opacity: disabled ? 0.6 : 1,
          boxShadow: opened
            ? `0px 0px 0px 2px ${theme.colors[borderColor][4]}40`
            : undefined,
          borderRadius: theme.radius.md,
          overflow: 'hidden',
          padding:
            type === 'single'
              ? `2px ${theme.spacing.sm}`
              : CustomMultiPill
                ? '4px 5px'
                : '4px 10px',
          minHeight: '2.25rem',
          cursor: disabled ? undefined : 'pointer',
          backgroundColor:
            backgroundColor ||
            (loading || disabled
              ? isDarkTheme
                ? theme.colors[color][8]
                : theme.colors[color][1]
              : isDarkTheme
                ? theme.colors[color][9]
                : c
                  ? theme.colors[c][0] + 90
                  : theme.white),
        };
      }}
    >
      {loadingQuery && value ? (
        <LoadingIndicator size="xs" sx={{ justifySelf: 'flex-end' }} />
      ) : (
        <>
          {type === 'single' ? (
            CustomSingleText ? (
              <CustomSingleText item={value} />
            ) : (
              <ValueLabel
                label={value?.label}
                placeholder={placeholder}
                placeholderColor={placeholderColor}
                loadingValue={!!loadingQuery}
                icon={value?.icon}
                error={!!error}
                lineClamp={lineClamp}
              />
            )
          ) : (
            <Group
              gap={rem(8)}
              my={3}
              pl={!value.length ? 4 : 1}
              sx={{
                maxWidth: '90%',
              }}
            >
              {value?.length ? (
                value.map((item) =>
                  CustomMultiPill ? (
                    <CustomMultiPill key={item.value} {...item} />
                  ) : (
                    <ValuePill key={item.value} value={item} />
                  )
                )
              ) : (
                <ValueLabel
                  label={null}
                  placeholder={placeholder}
                  placeholderColor={placeholderColor}
                  icon={null}
                  loadingValue={!!loadingQuery}
                  error={!!error}
                />
              )}
            </Group>
          )}
          {loadingMutation ? (
            <LoadingIndicator size="xs" />
          ) : withClearButton &&
            (Array.isArray(value) ? value.length : value) &&
            !disabled ? (
            <Center
              component="span"
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={(event) => {
                event.stopPropagation();

                if (type === 'single') {
                  setValue(null, {
                    type: 'removed',
                    value: value as SelectItem<TSelectValue>,
                  });
                  return;
                }

                setValue([], {
                  type: 'removed',
                  value: value as SelectItem<TSelectValue>[] as any,
                });
              }}
              style={{
                flexShrink: 0,
                width: rem(18),
                height: rem(18),
                cursor: 'pointer',
              }}
              title={clearButtonLabel}
              aria-label={clearButtonLabel}
              role="button"
            >
              <Icon
                icon="CrossIcon"
                color={(theme) => theme.colors.neutral[4]}
                size={18}
              />
            </Center>
          ) : (
            !disabled && (
              <ChevronIcon
                size={18}
                color={colors.gray[4]}
                sx={{
                  flexShrink: 0,
                  marginLeft: 'auto',
                }}
              />
            )
          )}
        </>
      )}
    </Group>
  );

  if (typeof error === 'string') {
    return (
      <Tooltip label={error} withArrow>
        {target}
      </Tooltip>
    );
  }

  return target;
};

const ValuePill = ({ value }: { value: SelectItem }) => {
  return (
    <Badge
      sx={{
        maxWidth: 140,
        margin: 0,
      }}
      leftIcon={value?.icon}
    >
      {value.label}
    </Badge>
  );
};

const ValueLabel = ({
  label,
  placeholder,
  placeholderColor,
  loadingValue,
  icon,
  error,
  lineClamp = 3,
}: {
  label: Maybe<string>;
  icon: Maybe<React.ReactNode>;
  placeholder: Maybe<string>;
  placeholderColor: string | undefined;
  loadingValue: boolean;
  error: boolean;
  lineClamp?: number;
}) => {
  const { colors } = useMantineTheme();

  if (loadingValue)
    return <LoadingIndicator sx={{ justifySelf: 'flex-end' }} size="xs" />;

  if (!label && !placeholder) return null;

  if (icon) {
    return (
      <Group wrap="nowrap" gap="xs">
        <Center sx={{ flexShrink: 0 }}>{icon}</Center>
        <Text
          component="span"
          size="sm"
          c={label ? undefined : placeholderColor || colors.gray[5]}
          lineClamp={lineClamp === 1 ? undefined : lineClamp}
        >
          {label || placeholder}
        </Text>
      </Group>
    );
  }

  return (
    <Text
      component="span"
      size="sm"
      c={error ? 'red' : label ? undefined : placeholderColor || colors.gray[5]}
      lineClamp={lineClamp === 1 ? undefined : lineClamp}
      truncate={lineClamp === 1}
    >
      {label || placeholder}
    </Text>
  );
};
